import React, { useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { isOpenCall, getStatusBadgeClasses, parseProblems, formatDateFr } from './ipHelpers';

const ticketTitle = (call) => {
  const problems = parseProblems(call.problems);
  if (problems.length) return problems[0];
  return call.requestType || 'Intervention';
};

function ToTreat({ calls, onSelectTicket }) {
  const open = useMemo(
    () => calls.filter(isOpenCall).sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)),
    [calls]
  );

  const hasOpen = open.length > 0;
  return (
    <Card className={hasOpen ? 'border-amber-200 dark:border-amber-900/50' : 'border-green-200 dark:border-green-900/50'}>
      <CardContent className="pt-5">
        <h3 className={`flex items-center gap-2 font-semibold mb-3 ${hasOpen ? 'text-amber-700 dark:text-amber-300' : 'text-green-700 dark:text-green-300'}`}>
          {hasOpen ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          À traiter ({open.length})
        </h3>
        {!hasOpen ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Aucune intervention ouverte. 🎉</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {open.map((call) => (
              <li key={call.id}>
                <button
                  type="button"
                  onClick={() => onSelectTicket?.(call)}
                  className="w-full text-left py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 -mx-2 px-2 rounded-md transition-colors"
                >
                  <div className="font-medium text-gray-800 dark:text-gray-100 text-sm leading-snug">{ticketTitle(call)}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {[call.brand, call.furnitures, formatDateFr(call.date, { day: '2-digit', month: 'short' })].filter(Boolean).join(' · ')}
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    {call.status && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusBadgeClasses(call.status)}`}>{call.status}</span>
                    )}
                    {call.issueKey && <span className="text-[11px] font-mono text-gray-400 dark:text-gray-500">{call.issueKey}</span>}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function RecentActivity({ calls }) {
  const recent = useMemo(
    () => [...calls].filter((c) => c.date).sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 6),
    [calls]
  );

  if (recent.length === 0) return null;

  return (
    <Card>
      <CardContent className="pt-5">
        <h3 className="font-semibold text-gray-800 dark:text-white mb-3">Activité récente</h3>
        <ol className="relative border-l border-gray-200 dark:border-gray-700 ml-1.5 space-y-4">
          {recent.map((call) => (
            <li key={call.id} className="ml-4">
              <span className="absolute -left-[5px] w-2.5 h-2.5 rounded-full bg-blue-500 border-2 border-white dark:border-gray-800" />
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200">{formatDateFr(call.date, { day: '2-digit', month: 'long', year: 'numeric' })}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {[call.brand, call.status || call.requestType || call.projectName].filter(Boolean).join(' · ')}
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

export default function IPSidebar({ calls, onSelectTicket }) {
  return (
    <div className="space-y-4 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
      <ToTreat calls={calls} onSelectTicket={onSelectTicket} />
      <RecentActivity calls={calls} />
    </div>
  );
}
